import { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Navbar, Label } from 'src/shared';
import { useAppDispatch } from 'src/hooks';
import { createSetupCheckoutSession } from 'src/api/apiCaller';
import { RoutesEnum } from 'src/types/navigation';
import { setSnackbarAttributes } from 'src/redux/snackbar/slice';
import { ClientSelectors } from 'src/redux/client/selectors';
import { selectProject } from 'src/redux/client/service';
import { signedIn } from 'src/redux/auth/service';
import { getAllProjects } from 'src/redux/projects/service';
import { getAllProjectsFromState } from 'src/redux/projects/selectors';
import { userHasAccessToProject } from 'src/utils/projectAccess';
import InvoicesTable from './components/InvoicesTable';
import {
    Root,
    PageHeader,
    BillingContentRoot,
    SectionHeader,
    AddPaymentButton,
    StyledAddCardIcon,
    InvoicesLoadingContainer,
    InvoicesLoadingSpinner,
    SelectProjectEmptyState,
    S
} from './styled';

function checkoutReturnUrls(projectId: number): {
    success_url: string;
    cancel_url: string;
} {
    const origin = window.location.origin;
    const base = `${origin}${RoutesEnum.BILLING}`;
    return {
        success_url: `${base}?checkout_setup=success&project_id=${projectId}`,
        cancel_url: `${base}?checkout_setup=cancelled&project_id=${projectId}`
    };
}

const Billing: FunctionComponent = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { selectedProjectId } = ClientSelectors();
    const projects = useSelector(getAllProjectsFromState);
    const [searchParams, setSearchParams] = useSearchParams();
    const [addPaymentLoading, setAddPaymentLoading] = useState(false);
    /** True only after `getAllProjects` resolves successfully (not used for unauthenticated skip). */
    const [projectsFetchDone, setProjectsFetchDone] = useState(false);
    const [projectsFetchError, setProjectsFetchError] = useState(false);

    useEffect(() => {
        if (!signedIn()) {
            navigate(RoutesEnum.LOGIN, { replace: true });
        }
    }, [navigate]);

    useEffect(() => {
        if (!signedIn()) {
            setProjectsFetchDone(false);
            setProjectsFetchError(false);
            return;
        }
        setProjectsFetchError(false);
        dispatch(getAllProjects())
            .unwrap()
            .then(() => {
                setProjectsFetchDone(true);
                setProjectsFetchError(false);
            })
            .catch(() => {
                setProjectsFetchDone(false);
                setProjectsFetchError(true);
            });
    }, [dispatch]);

    useEffect(() => {
        if (!signedIn()) {
            return;
        }
        if (!projectsFetchDone || projectsFetchError) {
            return;
        }
        if (!selectedProjectId) {
            return;
        }
        if (!userHasAccessToProject(selectedProjectId, projects)) {
            dispatch(
                setSnackbarAttributes({
                    snackbarOpen: true,
                    snackbarType: 'error',
                    snackbarMessage:
                        'You do not have access to billing for this project.'
                })
            );
            navigate(RoutesEnum.CLIENT_DASHBOARD, { replace: true });
        }
    }, [
        dispatch,
        navigate,
        projectsFetchDone,
        projectsFetchError,
        projects,
        selectedProjectId
    ]);

    useEffect(() => {
        const checkoutStatus = searchParams.get('checkout_setup');
        if (!checkoutStatus) return;

        const projectIdFromUrl = searchParams.get('project_id');
        if (projectIdFromUrl) {
            dispatch(selectProject(projectIdFromUrl));
        }

        if (checkoutStatus === 'success') {
            dispatch(
                setSnackbarAttributes({
                    snackbarOpen: true,
                    snackbarType: 'success',
                    snackbarMessage: 'Payment method saved.'
                })
            );
        } else if (checkoutStatus === 'cancelled') {
            dispatch(
                setSnackbarAttributes({
                    snackbarOpen: true,
                    snackbarType: 'info',
                    snackbarMessage: 'Payment setup cancelled.'
                })
            );
        }

        const next = new URLSearchParams(searchParams);
        next.delete('checkout_setup');
        next.delete('project_id');
        setSearchParams(next, { replace: true });
    }, [dispatch, searchParams, setSearchParams]);

    const handleAddPaymentMethod = useCallback(async () => {
        if (!selectedProjectId) return;

        const projectIdNum = Number(selectedProjectId);
        setAddPaymentLoading(true);
        try {
            const { success_url, cancel_url } =
                checkoutReturnUrls(projectIdNum);
            const data = await createSetupCheckoutSession(
                projectIdNum,
                success_url,
                cancel_url
            );
            window.location.assign(data.url);
        } catch (error) {
            console.error('Failed to start payment setup:', error);
            dispatch(
                setSnackbarAttributes({
                    snackbarOpen: true,
                    snackbarType: 'error',
                    snackbarMessage: 'Could not start payment setup'
                })
            );
        } finally {
            setAddPaymentLoading(false);
        }
    }, [dispatch, selectedProjectId]);

    const showInvoicesLoading =
        signedIn() &&
        !projectsFetchError &&
        !projectsFetchDone &&
        Boolean(selectedProjectId);
    const canViewInvoicesForProject =
        Boolean(selectedProjectId) &&
        userHasAccessToProject(selectedProjectId, projects);

    let invoicesSection = (
        <SelectProjectEmptyState>
            Select a project to view invoices
        </SelectProjectEmptyState>
    );
    if (showInvoicesLoading) {
        invoicesSection = (
            <InvoicesLoadingContainer>
                <InvoicesLoadingSpinner size={36} />
            </InvoicesLoadingContainer>
        );
    } else if (projectsFetchError) {
        invoicesSection = (
            <SelectProjectEmptyState>
                Could not load projects. Refresh the page or try again later.
            </SelectProjectEmptyState>
        );
    } else if (canViewInvoicesForProject && selectedProjectId) {
        invoicesSection = (
            <InvoicesTable
                key={selectedProjectId}
                projectId={selectedProjectId}
            />
        );
    }

    return (
        <Navbar>
            <Root sx={S.rootContainer}>
                <PageHeader>
                    <Label sxOverrides={S.pageTitle}>Billing</Label>
                </PageHeader>

                <BillingContentRoot>
                    <SectionHeader>
                        <Label sxOverrides={S.sectionTitle}>Invoices</Label>
                        <AddPaymentButton
                            type="button"
                            variant="outlined"
                            disableElevation
                            endIcon={<StyledAddCardIcon />}
                            disabled={
                                !selectedProjectId ||
                                addPaymentLoading ||
                                !projectsFetchDone ||
                                projectsFetchError ||
                                (Boolean(selectedProjectId) &&
                                    !userHasAccessToProject(
                                        selectedProjectId,
                                        projects
                                    ))
                            }
                            onClick={handleAddPaymentMethod}
                        >
                            {addPaymentLoading
                                ? 'Redirecting…'
                                : 'Add payment method'}
                        </AddPaymentButton>
                    </SectionHeader>

                    {invoicesSection}
                </BillingContentRoot>
            </Root>
        </Navbar>
    );
};

export default Billing;
